import type { FC } from 'react'
import type { SceneProps } from '@/components/landing/games/scene-ui'

import { useState } from 'react'
import { Crown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useSequence } from '@/hooks/use-sequence'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { SceneLabel } from '@/components/landing/games/scene-ui'

const SEGMENTS = [0, 1.5, 0, 2, 0, 1.5, 0, 1.7, 0, 3, 0, 1.5, 0, 2, 0, 1.7, 0, 1.5, 0, 4]
const SEGMENT = 360 / SEGMENTS.length
const CROWN = SEGMENTS.length - 1
const TIERS = [4, 3, 2, 1.7, 1.5, 0]

const TIER_FILLS: Record<string, string> = {
    '0': '#17171c',
    '1.5': '#2d2d34',
    '1.7': '#4c4b52',
    '2': '#8f8d88',
    '3': '#F4F2EE',
    '4': '#FF5B1F'
}

const fixed = (value: number): string => value.toFixed(2)

const point = (angle: number, radius: number): string => {
    const radians = (angle * Math.PI) / 180
    return `${fixed(100 + radius * Math.sin(radians))} ${fixed(100 - radius * Math.cos(radians))}`
}

const PATHS = SEGMENTS.map((value, index) => {
    const start = index * SEGMENT
    const end = start + SEGMENT

    return {
        value,
        d: `M ${point(start, 96)} A 96 96 0 0 1 ${point(end, 96)} L ${point(end, 52)} A 52 52 0 0 0 ${point(start, 52)} Z`
    }
})

/** Rotation that brings a segment's centre under the pointer at the top. */
const restingRotation = (index: number): number => 360 - (index * SEGMENT + SEGMENT / 2)

const POSTER_RESULT = 3

const WheelScene: FC<SceneProps> = ({ active }) => {
    const reduced = useReducedMotion()
    const [rotation, setRotation] = useState(restingRotation(POSTER_RESULT))
    const [result, setResult] = useState<number | null>(POSTER_RESULT)

    useSequence(active, async ({ wait }) => {
        const index = Math.floor(Math.random() * SEGMENTS.length)
        const jitter = (Math.random() - 0.5) * SEGMENT * 0.6

        setResult(null)
        setRotation(previous => previous - (previous % 360) + 360 * 5 + restingRotation(index) + jitter)

        if (!(await wait(3900))) return

        setResult(index)
        await wait(1600)
    })

    const value = result === null ? null : SEGMENTS[result]

    return (
        <div className='absolute inset-0 flex items-center justify-center gap-6 px-6 pb-6 pt-14'>
            <div className='relative aspect-square h-full max-h-[226px]'>
                <div aria-hidden='true' className='absolute inset-[10%] rounded-full bg-ember/10 blur-2xl' />

                <div
                    className={cn('absolute inset-0', !reduced && 'transition-transform duration-[3800ms] ease-[cubic-bezier(0.12,0.65,0.06,1)]')}
                    style={{ transform: `rotate(${rotation}deg)` }}
                >
                    <svg viewBox='0 0 200 200' aria-hidden='true' className='size-full'>
                        <circle cx='100' cy='100' r='99' fill='#0c0c0f' stroke='rgba(255, 255, 255, 0.1)' />
                        {PATHS.map((segment, index) => (
                            <path
                                key={index}
                                d={segment.d}
                                fill={TIER_FILLS[String(segment.value)]}
                                stroke='#08080A'
                                strokeWidth='1.4'
                                className={cn('transition-opacity duration-300', result !== null && result !== index && 'opacity-55')}
                            />
                        ))}
                    </svg>

                    <div className='absolute inset-0' style={{ transform: `rotate(${CROWN * SEGMENT + SEGMENT / 2}deg)` }}>
                        <Crown aria-hidden='true' strokeWidth={2.2} className='absolute left-1/2 top-[6%] size-3.5 -translate-x-1/2 text-ink' />
                    </div>
                </div>

                <div className='absolute inset-[27%] grid place-items-center rounded-full border border-white/10 bg-ink-1 shadow-[0_10px_30px_-10px_rgba(0,0,0,0.9)]'>
                    <span className={cn('font-mono text-base font-medium tabular-nums', value === null ? 'text-steel' : value === 0 ? 'text-steel' : value >= 4 ? 'text-ember' : 'text-bone')}>
                        {value === null ? '···' : `×${value.toFixed(2)}`}
                    </span>
                </div>

                <div aria-hidden='true' className='absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.8)]'>
                    <div className='size-0 border-x-[8px] border-t-[12px] border-x-transparent border-t-bone' />
                </div>
            </div>

            <div className='hidden w-[104px] shrink-0 flex-col gap-2 @sm:flex'>
                <SceneLabel>Segments</SceneLabel>
                {TIERS.map(tier => (
                    <div key={tier} className={cn('flex items-center justify-between font-mono text-[11px] transition-colors duration-300', value === tier ? 'text-bone' : 'text-steel')}>
                        <span className='flex items-center gap-2'>
                            <span className='size-2.5 rounded-sm ring-1 ring-inset ring-white/10' style={{ background: TIER_FILLS[String(tier)] }} />
                            {tier === 4 ? 'Crown' : tier === 0 ? 'Miss' : 'Pay'}
                        </span>
                        <span>×{tier.toFixed(tier % 1 === 0 ? 0 : 1)}</span>
                    </div>
                ))}
            </div>
        </div>
    )
}

export default WheelScene
